import spinnerIcon from '@/assets/copilot/spinner.svg'
import { ExternalLinkIcon } from '@/components/Icons'
import { useOpenBacklogConnect } from '@/components/backlogConnectContext'
import { SETTINGS_BACKLOG_PATH } from '@/constants/settings-tabs'
import { useUiStrings } from '@/hooks/useUiStrings'
import type { CopilotSpan, Suggestion } from './copilot.types'

type AssistAnswerBodyProps = {
  suggestion: Suggestion
}

/** Shared so the two ways of offering this cannot drift apart visually. */
const CONNECT_BACKLOG_CLASS =
  'mt-3 inline-flex items-center gap-1 rounded bg-blue-50 px-2 py-1 text-[11px] font-semibold text-blue-700 hover:bg-blue-100 dark:bg-[var(--accent-light)] dark:text-[var(--accent)]'

/**
 * One line of answer text with the AI's own emphasis kept.
 *
 * Index keys: the runs have no identity of their own and the list is rebuilt
 * whole whenever the answer changes, so there is nothing for a stable key to
 * preserve.
 */
function AnswerText({ spans }: { spans: CopilotSpan[] }) {
  return (
    <>
      {spans.map((span, index) =>
        span.bold ? (
          <strong key={index} className="font-semibold">
            {span.text}
          </strong>
        ) : (
          <span key={index}>{span.text}</span>
        )
      )}
    </>
  )
}

/**
 * Body of an answer: heading, loading indicator, the answer itself and
 * reference links. Shared by the standalone answer card and by the answer kept
 * inside the active suggestion card, so both stay identical.
 *
 * The answer is drawn block by block. It used to be one `•` per line, which is
 * what BRAI-604 reported: a wrapped paragraph came out as a column of bullets
 * that ran down the panel and could not be scanned.
 */
export default function AssistAnswerBody({ suggestion }: AssistAnswerBodyProps) {
  const { t } = useUiStrings()
  const openBacklogConnect = useOpenBacklogConnect()

  return (
    <>
      <p className="text-[12px] font-bold uppercase leading-4 tracking-[0.3px] text-[#334155] dark:text-[var(--text-secondary)]">
        {t.copilotAnswerHeading}
      </p>
      {suggestion.isLoading ? (
        // Figma's Material 3 indeterminate indicator is a static SVG that the
        // design spins; the label is carried as alt text, not visible copy.
        <div className="mt-[11.5px]">
          <img
            src={spinnerIcon}
            alt={t.copilotSearchingAnswer}
            className="mx-auto block animate-spin motion-reduce:animate-none"
          />
        </div>
      ) : suggestion.answer ? (
        <>
          <div className="mt-2 space-y-2 text-xs leading-relaxed text-slate-700 dark:text-[var(--text-secondary)]">
            {suggestion.answer.blocks.map((block, index) => {
              if (block.kind === 'heading') {
                return (
                  <p
                    key={index}
                    className="font-semibold text-slate-900 dark:text-[var(--text-primary)]"
                  >
                    <AnswerText spans={block.spans} />
                  </p>
                )
              }

              if (block.kind === 'list') {
                return (
                  <ul key={index} className="space-y-1.5">
                    {block.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex gap-2">
                        <span aria-hidden="true">•</span>
                        <span>
                          <AnswerText spans={item} />
                        </span>
                      </li>
                    ))}
                  </ul>
                )
              }

              return (
                <p key={index}>
                  <AnswerText spans={block.spans} />
                </p>
              )
            })}
          </div>
          {suggestion.answer.action?.kind === 'connect-backlog' ? (
            openBacklogConnect ? (
              // Approval in a window of its own, from right here (BRAI-689).
              // It used to be a second tab onto Settings, which left the
              // reader to find their way back to a panel that had not noticed
              // they had connected.
              <button
                type="button"
                onClick={openBacklogConnect}
                data-testid="assist-connect-backlog"
                className={CONNECT_BACKLOG_CLASS}
              >
                {t.copilotSetupConnectAction}
              </button>
            ) : (
              // No dialog on this screen. A new tab, not a route change: the
              // screen behind this panel may be a running recording, which the
              // app already fights to keep a reader on (`beforeunload`, and the
              // Back button is intercepted), and an in-app link here would
              // unmount it and take the recording with it.
              <a
                href={SETTINGS_BACKLOG_PATH}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="assist-connect-backlog"
                className={CONNECT_BACKLOG_CLASS}
              >
                {t.assistOpenSettingsNewTab}
                <ExternalLinkIcon className="h-3 w-3" />
              </a>
            )
          ) : null}
          {suggestion.answer.references && suggestion.answer.references.length > 0 ? (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {suggestion.answer.references.map((ref, index) => (
                <a
                  key={ref.id}
                  href={ref.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-blue-600 hover:bg-blue-50 dark:bg-white/10 dark:text-[var(--accent)]"
                  title={ref.label}
                >
                  [{index + 1}]
                  <ExternalLinkIcon className="h-3 w-3" />
                </a>
              ))}
            </div>
          ) : null}
        </>
      ) : null}
    </>
  )
}
